import { fetchAPI, API_BASE_URL } from "./apiCore";
import { getToken } from "./auth";

const PATIENT_SORT_FIELD_MAP = {
  name: "LAST_NAME",
  lastname: "LAST_NAME",
  lastName: "LAST_NAME",
  firstname: "FIRST_NAME",
  firstName: "FIRST_NAME",
  birthDate: "BIRTH_DATE",
  patientAccountNumber: "PATIENT_ACCOUNT_NUMBER",
  redcapMatchedAt: "REDCAP_MATCHED_AT",
  gender: "GENDER",
  state: "STATE",
  redcapMatchStatus: "REDCAP_MATCH_STATUS",
};

const normalizePatientPage = (payload, fallbackPage = 1, fallbackSize = 10) => {
  if (Array.isArray(payload?.content)) {
    return {
      ...payload,
      pageNumber:
        typeof payload.pageNumber === "number"
          ? payload.pageNumber + 1
          : fallbackPage,
      pageSize:
        typeof payload.pageSize === "number" ? payload.pageSize : fallbackSize,
    };
  }

  if (Array.isArray(payload?.patients)) {
    const pageNumber = typeof payload.page === "number" ? payload.page : fallbackPage;
    const pageSize = typeof payload.limit === "number" ? payload.limit : fallbackSize;
    const totalElements =
      typeof payload.total === "number" ? payload.total : payload.patients.length;

    return {
      content: payload.patients,
      totalElements,
      pageNumber,
      pageSize,
      totalPages:
        typeof payload.totalPages === "number"
          ? payload.totalPages
          : pageSize > 0
            ? Math.ceil(totalElements / pageSize)
            : 0,
      hasNext: payload.hasNext,
      hasPrevious: payload.hasPrevious,
    };
  }

  return {
    content: [],
    totalElements: 0,
    pageNumber: fallbackPage,
    pageSize: fallbackSize,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  };
};

const normalizeLegacyPageToZeroBased = (page) => {
  if (typeof page !== "number" || Number.isNaN(page)) {
    return 0;
  }
  return Math.max(page - 1, 0);
};

const mapLegacySortField = (sortBy) => {
  if (typeof sortBy !== "string" || !sortBy.trim()) {
    return "LAST_NAME";
  }
  return PATIENT_SORT_FIELD_MAP[sortBy.trim()] || "LAST_NAME";
};

const mapLegacySortDirection = (sortOrder) => {
  return String(sortOrder).toUpperCase() === "DESC" ? "DESC" : "ASC";
};

export const fetchPatientDirectory = async (params = {}) => {
  const queryParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      acc[key] = value;
    }
    return acc;
  }, {});

  return fetchAPI("biobank/patients/directory", "GET", queryParams);
};

export const fetchPatientContactById = async (patientId) => {
  return fetchAPI(`biobank/patients/id/${encodeURIComponent(patientId)}`, "GET");
};

// --- End New Follow-Up APIs ---


// confirm patient
export const fetchAllPatients = async () => {
  try {
    return await fetchAPI("biobank/patients", "GET");
  } catch (error) {
    console.error("API fetch error (patients):", error);
    return [];
  }
};

// Fetch patient by account number
export const fetchPatientByAccountNumber = async (accountNumber) => {
  try {
    console.log("🌐 API: Fetching patient by account number:", accountNumber);

    const result = await fetchAPI(
      `biobank/patients/${encodeURIComponent(accountNumber)}`,
      "GET"
    );

    console.log("🌐 API: Patient by account number response:", result);
    return result || null;
  } catch (error) {
    console.error("❌ API: Error fetching patient by account number:", error);
    console.error("❌ API: Error details:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.response?.url,
    });
    return null;
  }
};

// Fetch patient by patient ID
export const fetchPatientById = async (patientId) => {
  try {
    console.log("🌐 API: Fetching patient by ID:", patientId);

    const result = await fetchAPI(
      `biobank/patients/id/${encodeURIComponent(patientId)}`,
      "GET"
    );

    console.log("🌐 API: Patient by ID response:", result);
    return result || null;
  } catch (error) {
    console.error("❌ API: Error fetching patient by ID:", error);
    console.error("❌ API: Error details:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.response?.url,
    });
    return null;
  }
};

// Create Patient
export const createPatient = async (patientData) => {
  try {
    return await fetchAPI("biobank/patients", "POST", {}, patientData);
  } catch (error) {
    console.error("Error creating patient:", error);
    throw error;
  }
};

//updatePatient
export const updatePatient = async (patientId, payload) => {
  return fetchAPI(`biobank/patients/${patientId}`, "PATCH", {}, payload);
};

// Fetch patient data with filtering and pagination
export const fetchPatientData = async (params = {}) => {
  const resolvedPage = params.page ?? 1;
  const resolvedLimit = params.limit ?? 10;

  try {
    const {
      page = 1,
      limit = 10,
      search = "",
      irbStudies = [], // Array of IRB study IDs to filter by
      sortBy = "name",
      sortOrder = "asc",
    } = params;

    const request = {
      page: normalizeLegacyPageToZeroBased(page),
      size: limit,
      filters: {},
      sort: {
        field: mapLegacySortField(sortBy),
        direction: mapLegacySortDirection(sortOrder),
      },
    };

    if (search.trim()) {
      request.filters.patientAccountNumber = search.trim();
    }

    if (irbStudies.length > 0) {
      request.filters.irbStudies = irbStudies;
    }

    const response = await fetchAPI("admin/patients/search", "POST", {}, request);
    return normalizePatientPage(response, page, limit);
  } catch (error) {
    console.error("Error fetching patient data:", error);
    return normalizePatientPage(null, resolvedPage, resolvedLimit);
  }
};

// CONSENT CHECKING API FUNCTIONS

// Check if patient has active consent for all encounter studies
// Returns allConsentsComplete: true only if patient has consented to ALL studies in the encounter
export const checkPatientConsents = async (patientId, studyIds) => {
  try {
    console.log("🌐 API: Checking patient consent eligibility", {
      patientId,
      studyIds,
    });
    const query = new URLSearchParams({
      patientId,
    });
    const result = await fetchAPI(
      `biobank/consent/eligibility/preview?${query.toString()}`,
      "GET"
    );

    const effectiveStudies = Array.isArray(result?.effectiveActiveStudies)
      ? result.effectiveActiveStudies
      : [];
    const effectiveStudyIds = effectiveStudies
      .map((item) => item?.irbStudy)
      .filter(Boolean);
    const missingConsents = Array.isArray(studyIds)
      ? studyIds.filter((studyId) => !effectiveStudyIds.includes(studyId))
      : [];

    const mappedResult = {
      allConsentsComplete:
        Array.isArray(studyIds) && studyIds.length > 0
          ? missingConsents.length === 0
          : Boolean(result?.canCheckIn),
      hasActiveConsents: effectiveStudyIds.length > 0,
      missingConsents,
      activeConsents: effectiveStudies,
      allowedKitTypes: result?.allowedKitTypes || [],
      effectiveActiveStudies: effectiveStudies,
      canCheckIn: Boolean(result?.canCheckIn),
    };

    console.log("🌐 API: Consent eligibility response", mappedResult);
    return mappedResult;
  } catch (error) {
    console.error("❌ API: Error checking patient consents:", error);
    console.error("❌ API: Error details:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.response?.url,
    });
    return {
      hasActiveConsents: false,
      missingConsents: studyIds,
      allConsentsComplete: false,
      activeConsents: [],
      allowedKitTypes: [],
      effectiveActiveStudies: [],
      canCheckIn: false,
    };
  }
};

// Fetch all consents for a specific patient
export const fetchPatientConsents = async (patientId) => {
  try {
    console.log("🌐 API: Fetching patient consents", { patientId });

    const result = await fetchAPI(
      `biobank/consent/patient/${patientId}`,
      "GET"
    );

    console.log("🌐 API: Patient consents response", result);
    return result || [];
  } catch (error) {
    console.error("❌ API: Error fetching patient consents:", error);
    console.error("❌ API: Error details:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.response?.url,
    });
    return [];
  }
};

// Fetch consent overview for a specific patient
export const fetchConsentOverview = async (patientId) => {
  try {
    console.log("🌐 API: Fetching consent overview", { patientId });

    const result = await fetchAPI(
      `biobank/patients/${patientId}/consent-overview`,
      "GET"
    );

    console.log("🌐 API: Consent overview response", result);
    return result || null;
  } catch (error) {
    console.error("❌ API: Error fetching consent overview:", error);
    console.error("❌ API: Error details:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.response?.url,
    });
    return null;
  }
};

// Fetch patient/participant activity timeline
export const fetchPatientActivity = async (patientId) => {
  try {
    if (!patientId) return [];
    const result = await fetchAPI(
      `biobank/patients/${encodeURIComponent(patientId)}/activity`,
      "GET"
    );
    return Array.isArray(result) ? result : [];
  } catch (error) {
    console.error("Error fetching patient activity:", error);
    return [];
  }
};

// Fetch patient sample history (barcode id, sample type, collection date, total aliquots created)
export const fetchPatientSampleHistory = async (patientId) => {
  try {
    if (!patientId) return [];
    const result = await fetchAPI(
      `biobank/patients/${encodeURIComponent(patientId)}/sample-history`,
      "GET"
    );
    return Array.isArray(result) ? result : [];
  } catch (error) {
    console.error("Error fetching patient sample history:", error);
    return [];
  }
};

// Revoke a consent
export const revokeConsent = async (consentId, reason) => {
  return await fetchAPI(`biobank/consent/${consentId}/revoke`, "POST", {}, { reason });
};

// --- Genomic Reports (Patient Reports) ---

// Fetch all reports for a patient
export const fetchPatientReports = async (patientId) => {
  const result = await fetchAPI(
    `biobank/patients/${encodeURIComponent(patientId)}/reports`,
    "GET",
    { page: 0, size: 100 }
  );
  return Array.isArray(result) ? result : result?.content ?? [];
};

export const uploadVcfForPatient = async (patientId, mrn, file, onProgress) => {
  const token = await getToken();
  if (!token) {
    throw new Error(
      "Your session has expired. Please refresh the page and log in again before uploading."
    );
  }

  let fileText = "";
  try {
    fileText = await file.text();
  } catch (e) {
    throw new Error("Could not read the selected file. Please try again.");
  }

  console.log("[uploadVcfForPatient] file size:", file.size, "bytes | first 150 chars:", fileText.substring(0, 150));

  const vcfBlob = new Blob([fileText], { type: "application/octet-stream" });
  const formData = new FormData();
  formData.append("file", vcfBlob, file.name);

  const uploadUrl = `${API_BASE_URL}/biobank/patients/${encodeURIComponent(patientId)}/reports`;
  console.log("[uploadVcfForPatient] POST →", uploadUrl, "| mrn (must be inside VCF):", mrn);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", uploadUrl);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.timeout = 10 * 60 * 1000; // 10 minutes for large files

    if (onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
    }

    xhr.onload = () => {
      const text = xhr.responseText;
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      console.log("[uploadVcfForPatient] HTTP", xhr.status, parsed ?? text);

      if (xhr.status === 401) {
        reject(new Error("Your session expired during upload. Refresh the page, log in, and retry."));
        return;
      }

      if (xhr.status === 403) {
        reject(new Error("You do not have permission to upload reports. Contact your administrator."));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        const status = parsed?.status;
        if (!status || status === "UPLOADED" || status === "DUPLICATE_FILE") {
          resolve(parsed ?? text);
          return;
        }
        if (status === "UNMATCHED_PATIENT") {
          const extracted = parsed?.extractedPatientAccountNumber;
          reject(new Error(
            extracted
              ? `The MRN in the VCF file (${extracted}) does not match any patient. Ensure the file's ##PatientAccountNumber header matches the patient's MRN (${mrn || "unknown"}).`
              : `The MRN extracted from the VCF does not match any patient. Ensure the file's ##PatientAccountNumber header matches MRN ${mrn || "unknown"}.`
          ));
          return;
        }
        if (status === "INVALID_FILE") {
          reject(new Error("The file is empty or not a valid VCF. Please check the file and try again."));
          return;
        }
        if (status === "UPLOAD_FAILED") {
          reject(new Error("The server failed to store the file. Please try again or contact support."));
          return;
        }
        
        resolve(parsed ?? text);
        return;
      }

      const msg =
        (parsed?.message && parsed.message !== "" ? parsed.message : null) ||
        parsed?.error ||
        parsed?.detail ||
        (typeof text === "string" && text.length < 500 ? text : null) ||
        `Upload failed — HTTP ${xhr.status}`;
      reject(new Error(typeof msg === "string" ? msg : `Upload failed — HTTP ${xhr.status}`));
    };

    xhr.onerror = () =>
      reject(new Error("Network error — upload could not reach the server."));
    xhr.ontimeout = () =>
      reject(new Error("Upload timed out after 10 minutes."));

    xhr.send(formData);
  });
};

